import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';
import { applyBuffs, selectStats } from 'logic/stats';
import { D2SkillTooltip } from 'tooltips';

import './Skill.scss';
import skillFunctions from 'logic/skills/functions';
import NumberInput from 'ui/number';
import { AuraFlags } from 'logic/skills/calc';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { StatTooltip, unitIcon } from 'components/stats/Stats';
import { Color } from 'common';
import SkillDamage from './Damage';
import SkillAnimation from './Animation';
import { isSubType, itemTypeNames } from 'logic/itemTypes';
import { createSummon } from 'logic/monster/summons';
import Tooltip from 'ui/tooltip';
import { statsToLines } from 'logic/item';
import GameContext from 'gamectx';
import SkillDPS from './DPS';
import { itemSkillTypes } from 'logic/skills/skillinfo';

const _L = Locale("ui-calculator");

export const displayUnitNames = {
  player: 'Player',
  mercenary: 'Mercenary',
  monster: 'Monster',
};

const disabledReason = {
  human: 'Requires human form',
  form: 'Requires {0} form',
  weapon: 'Requires {0}',
  offhand: 'Requires offhand weapon ({0})',
  target: 'Invalid target',
};

export function skillDisabled(skill, stats, target) {
  if (stats.unit !== 'player') return false;
  if (skill.aura) return false;

  if (skill.restrict === 2) {
    const forms = [skill.state1, skill.state2, skill.state3].filter(Boolean);
    if (!forms.some(state => stats.base.state?.[state])) return 'form';
  } else if (!skill.restrict) {
    if (stats.base.state && Object.keys(stats.base.state).some(state => Data.states[state]?.restrict)) return 'human';
  }

  const func = skillFunctions[skill.srvdofunc];
  if (func?.checkTarget && !func.checkTarget(target)) {
    return 'target';
  }

  function checkWeapon(weapon, include, exclude, defaultType) {
    const type = Data.items[weapon?.baseitem]?.type || defaultType;
    if (!type) return false;
    if (!include.some(t => t && isSubType(type, t))) return false;
    if (exclude.some(t => t && isSubType(type, t))) return false;
    return true;
  }

  if (skill.weapsel === 1) {
    if (skill.itypea1 && !checkWeapon(stats.weapon2, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2])) return 'offhand';
  } else if (skill.weapsel === 3) {
    if (skill.itypea1 && !checkWeapon(stats.weapon1, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2], 'weap')) return 'weapon';
    if (skill.itypeb1 && !checkWeapon(stats.weapon2, [skill.itypeb1, skill.itypeb2, skill.itypeb3], [skill.etypeb1, skill.etypeb2])) return 'offhand';
  } else if (skill.itypea1 === 'shld') {
    if (!stats.shieldbase) return 'weapon';
  } else if (skill.itypea1) {
    if (!checkWeapon(stats.weapon1, [skill.itypea1, skill.itypea2, skill.itypea3], [skill.etypea1, skill.etypea2], 'weap')) return 'weapon';
  }
}

export function SkillIcon({ id, color=0, size=48, onClick, ...props }) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  const gfx = React.useContext(GameContext).gfx;

  const [image, setImage] = React.useState();
  const [pressed, setPressed] = React.useState(false);

  React.useEffect(() => {
    const cel = (desc && (!desc.skillpage || skill.charclass)) ? desc.iconcel : 14;
    gfx.itemdc6(skill && Data.classSkillIcons[skill.charclass || '']).then(dc6 => gfx.makeBitmap(dc6, 9 * 21 + color, [cel, cel + 1])).then(icon => {
      if (!ref.current) return;
      setImage(icon);
    });
  }, [skill, desc, color, gfx]);

  const ref = React.useRef();
  React.useEffect(() => {
    const cel = (desc && (!desc.skillpage || skill.charclass)) ? desc.iconcel : 14;
    const frame = image?.frames[cel + (pressed ? 1 : 0)];
    if (!frame || !ref.current) return;
    const ctx = ref.current.getContext('2d');
    ctx.drawImage(image.bitmap, frame.x, frame.y, frame.width, frame.height, 0, 0, size, size);
  }, [image, desc, skill, size, pressed]);

  //if (!desc) return null;
  const events = {};
  events.onMouseDown = React.useCallback(e => {
    if (e.button === 0) {
      e.preventDefault();
      setPressed(true);
    }
  }, []);
  events.onClick = onClick;
  events.onDragStart = React.useCallback(e => e.preventDefault(), []);
  React.useEffect(() => {
    if (pressed) {
      function handler(e) {
        if (e.button === 0) {
          setPressed(false);
        }
      }
      document.addEventListener('mouseup', handler);
      return () => document.removeEventListener('mouseup', handler);
    }
  }, [pressed, id]);
  if (onClick) props = {...props, ...events};

  return <canvas className={classNames("d2p-SkillIcon", {"d2p-clickable": !!onClick})} width={size} height={size} ref={ref} {...props}/>;
}

const SkillTargets = React.forwardRef(function SkillTargets({ unit, id, onSelect, stats, children }, clickRef) {
  const [expanded, setExpanded] = React.useState(false);

  const skill = Data.skills[id];
  const func = skillFunctions[skill.srvdofunc || 0];

  const team = stats[unit].team;

  const validTargets = Object.keys(stats).filter(ounit => {
    if (!func.targetFilter) return true;
    const ostats = stats[ounit];
    if (!(func.targetFilter & ostats.aura_include) || (func.targetFilter & ostats.aura_exclude)) return false;
    if ((func.targetFilter & AuraFlags.TargetAllies) && team !== ostats.team) return false;
    if ((func.targetFilter & AuraFlags.IgnoreAllies) && team === ostats.team) return false;
    return true;
  });

  const ref = React.useRef();
  React.useEffect(() => expanded && ref.current.focus(), [expanded]);
  const onBlur = React.useCallback(e => {
    if (!e.relatedTarget || !ref.current.contains(e.relatedTarget)) {
      setExpanded(false);
    }
  }, []);

  React.useImperativeHandle(clickRef, () => ({
    click() {
      setExpanded(e => !e);
    }
  }));

  return (
    <span className={classNames("d2p-target-select", {"d2p-expanded": expanded})} onClick={() => setExpanded(e => !e)} ref={ref} tabIndex={-1} onBlur={onBlur}>
      {children} <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown} className="d2p-arrow"/>{!!expanded && (
      <div className="d2p-menu">
        {validTargets.map(t => (
          <label key={t} onClick={() => onSelect(t)}>
            <span className={`d2p-class-icon d2p-class-${unitIcon(t, stats[t].class)}`}/>
            {displayUnitNames[t] ? _L(displayUnitNames[t]) : stats[t]?.name}
          </label>
        ))}
      </div>
    )}</span>
  );
});

const BuffSettings = React.forwardRef(function BuffSettings({ unit, id, level, active }, clickRef) {
  const skill = Data.skills[id];
  const func = skillFunctions[skill.srvdofunc || 0];

  const stats = Store.useSelector(selectStats);
  const dispatch = Store.useDispatch();
  const onApply = React.useCallback(target => dispatch.setBuffs(buffs => (
    applyBuffs(buffs, stats, unit, id, level, target ? [target] : null))
  ), [dispatch, stats, unit, id, level]);

  const onCancel = React.useCallback(() => dispatch.setBuffs(buffs => {
    buffs = {...buffs};
    for (const uid in buffs) {
      if (buffs[uid].some(buff => buff.source === unit && buff.skill === id)) {
        buffs[uid] = buffs[uid].filter(buff => buff.source !== unit || buff.skill !== id);
      }
    }
    return buffs;
  }), [dispatch, unit, id]);

  const targetRef = React.useRef();
  React.useImperativeHandle(clickRef, () => ({
    click() {
      if (func.targetFilter) {
        targetRef.current?.click();
      } else {
        onApply();
      }
    }
  }));

  return (
    <div className="d2p-skill-settings">
      {func.targetFilter ? (
        <SkillTargets unit={unit} id={id} onSelect={onApply} stats={stats} ref={targetRef}>
          <span className="d2p-apply-buff">{_L`Apply buff`}</span>
        </SkillTargets>
      ) : (
        <span className="d2p-apply-buff" onClick={() => onApply()}>{active ? _L`Refresh skill` : _L`Activate skill`}</span>
      )}
      {active && ' / '}
      {active && <span className="d2p-cancel-buff" onClick={onCancel}>{_L`Remove buff`}</span>}
    </div>
  );
});

const SummonSettings = React.forwardRef(function SummonSettings({ id, target, level, active }, clickRef) {
  const stats = Store.useSelector(selectStats);
  const dispatch = Store.useDispatch();
  const onApply = React.useCallback(() => dispatch.setState(state => createSummon(state, stats, id, target, level)), [dispatch, stats, id, target, level]);

  const onCancel = React.useCallback(() => dispatch.setSummons(summons => {
    summons = {...summons};
    for (const sid in summons) {
      if (summons[sid].skill === id) {
        delete summons[sid];
      }
    }
    return summons;
  }), [dispatch, id]);

  React.useImperativeHandle(clickRef, () => ({
    click() {
      onApply();
    }
  }));

  let summonTip = active ? _L`Resummon` : _L`Summon`;
  if (Data.skills[id].srvdofunc === 58 && target) {
    const monster = Data.monsters[target.class];
    if (monster) summonTip = _L`Revive ${Locale.strings[monster.namestr]}`;
  }

  return (
    <div className="d2p-skill-settings">
      <span className="d2p-apply-buff" onClick={onApply}>{summonTip}</span>
      {active && ' / '}
      {active && <span className="d2p-cancel-buff" onClick={onCancel}>{_L`Unsummon`}</span>}
    </div>
  );
});

function SkillSettings({ activeKey, unit, id, stats }) {
  const skill = Data.skills[id];
  const func = skillFunctions[skill.srvdofunc || 0];
  const initial = React.useMemo(() => typeof func.active === 'function' ? func.active(unit) : func.active, [func, unit]);
  const active = {...initial, ...Store.useProfile(profile => profile.active[activeKey])};
  const dispatch = Store.useDispatch();

  const onActive = React.useCallback(e => dispatch.setProfile(profile => {
    profile = {...profile};
    profile.active = {...profile.active};
    profile.active[activeKey] = {...profile.active[activeKey], active: !!e.target.checked};
    const group = func.group?.(id);
    if (group && e.target.checked) {
      for (const oid in Data.skills) {
        if (id === oid) continue;
        if (!stats.skills[oid]) continue;
        const ofunc = skillFunctions[Data.skills[id].srvdofunc || 0];
        if (ofunc.group?.(oid) === group) {
          const prev = profile.active[`${unit}${oid}`];
          if (!prev || prev.active !== false) {
            profile.active[`${unit}${oid}`] = {...prev, active: false};
          }
        }
      }
    }
    return profile;
  }), [activeKey, dispatch, func, id, unit, stats]);

  const onStacks = React.useCallback(value => dispatch.profile.setActive(activeKey, active => ({...active, stacks: value})), [dispatch, activeKey]);
  const onCharges = React.useCallback(value => dispatch.profile.setActive(activeKey, active => ({...active, charges: value})), [dispatch, activeKey]);

  const maxStacks = React.useMemo(() => func.maxStacks?.(id, stats, stats.skills[id]) || stats.skills[id], [id, func, stats]);

  return (
    <ul className="d2p-skill-settings">
      {active.active != null && (
        <li><label><input type="checkbox" checked={active.active} onChange={onActive}/> {_L`Active`}</label></li>
      )}
      {active.stacks != null && (
        <li><label><NumberInput value={Math.min(active.stacks, maxStacks)} min={0} max={maxStacks} onChange={onStacks}/> {_L`Stacks`}</label></li>
      )}
      {active.charges != null && (
        <li><label><NumberInput value={active.charges} min={0} max={3} onChange={onCharges}/> {_L`Charges`}</label></li>
      )}
    </ul>
  );
}

function SkillLevelTip({ id, stats, unit }) {
  const stat = {
    name: _L`Level`,
    value: stats => stats.skills[id],
    tooltip: [
      `{sources:skills.${id}}`,
    ],
  };
  return <StatTooltip stat={stat} stats={stats} unit={unit}/>;
}

function SkillItemTip({ id, stats, unit, type, level, param }) {
  const sid = itemSkillTypes[type].stat;
  const isProc = Data.itemStatCost[sid]?.descfunc === 15;
  const sourceStat = (type === 'aura' ? `${sid}#${id}` : `${sid}#${id}#${level}`);
  const displayStat = (type === 'aura' ? sourceStat : `${sid}#${id}#${isProc ? param : level}`);
  const domain = (stats.weapon1.sources[sourceStat] ? 'weapon1' : (stats.weapon2?.sources[sourceStat] ? 'weapon2' : 'base'));
  const lines = statsToLines({[displayStat]: (isProc || type === 'aura') ? level : param})[0];
  const stat = {
    name: lines?.[1] || _L`Unknown`,
    percent: isProc,
    tooltip: [
      `{sources:${domain}.${sourceStat}}`,
    ],
  };
  return <StatTooltip stat={stat} stats={stats} unit={unit}/>;
}

export default React.memo(function SkillPanel({ activeKey, unit, stats, id, info, target, itemType, itemLevel, itemParam }) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  const func = skillFunctions[skill?.srvdofunc || 0];

  const level = itemLevel || stats.skills[id] || 0;

  const dispatch = Store.useDispatch();
  const onHide = React.useCallback(() => dispatch.profile.setActive(activeKey, active => {
    const initial = typeof func.active === 'function' ? func.active(unit) : func.active;
    active = {...initial, ...active};
    active.hidden = true;
    return active;
  }), [func, dispatch, unit, activeKey]);

  const isBuff = !!(func.showBuff ? func.showBuff(id, stats, stats.skills[id]) : func.buff);
  const buffs = Store.useSelector(state => isBuff ? state.buffs : null);
  const buffInactive = React.useMemo(() => {
    if (isBuff) {
      if (!buffs) return true;
      return !Object.values(buffs).some(list => list.some(buff => buff.source === unit && buff.skill === id));
    }
  }, [isBuff, buffs, unit, id]);

  const isSummon = (unit === 'player' && func.summon);
  const summons = Store.useSelector(state => isSummon ? state.summons : null);
  const summonInactive = React.useMemo(() => {
    if (isSummon) {
      return !Object.values(summons).some(sum => sum.skill === id);
    }
  }, [summons, id, isSummon]);

  const disabled = React.useMemo(() => skillDisabled(skill, stats, target), [skill, stats, target]);

  const clickRef = React.useRef();
  const summonRef = React.useRef();
  const onClick = React.useCallback(() => (clickRef.current || summonRef.current)?.click(), []);

  if (!skill || !func) return null;

  let color = Color.WHITE;
  if (disabled) color = Color.RED;
  else if (buffInactive) color = Color.GRAY;
  else if (summonInactive) color = Color.GRAY;
  else if (skill.aura) color = Color.GOLD;

  let disabledText = disabledReason[disabled] || disabled;
  switch (disabled) {
  case 'form':
    disabledText = _L(disabledText, [skill.state1, skill.state2, skill.state3].filter(Boolean).map(state =>
      Locale.strings[Data.monsterById[Data.states[state]?.gfxclass]?.namestr] || state).join(', '));
    break;
  case 'weapon':
    disabledText = _L(disabledText, [skill.itypea1, skill.itypea2, skill.itypea3].filter(Boolean).map(t => _L(itemTypeNames[t])).join(', '));
    break;
  case 'offhand':
    disabledText = _L(disabledText, [skill.itypeb1, skill.itypeb2, skill.itypeb3].filter(Boolean).map(t => _L(itemTypeNames[t])).join(', '));
    break;
  default:
    if (disabledText) {
      disabledText = _L(disabledText);
    }
  }

  return (
    <div className="d2p-SkillPanel">
      <D2SkillTooltip id={id} unit={unit} full={false} disabled={!desc} level={level}>
        <SkillIcon id={id} color={color} onClick={!disabled && (isBuff || isSummon) && onClick}/>
      </D2SkillTooltip>
      <div className="d2p-skill-info">
        <div className="d2p-skill-hide" onClick={onHide}>
          <FontAwesomeIcon icon={faEyeSlash}/>
        </div>
        <div className={`d2p-skill-name d2-color-${color}`}>
          {Locale.strings[desc?.strname] || skill.skill}
          {!!(skill.enhanceable && !itemType) && (
            <Tooltip tooltip={<SkillLevelTip id={id} stats={stats} unit={unit}/>}>
              <span className="d2p-skill-level">{' \u2013 '}{_L`Level ${level}`}</span>
            </Tooltip>
          )}
          {!!itemType && (
            <Tooltip tooltip={<SkillItemTip id={id} stats={stats} unit={unit} type={itemType} level={itemLevel} param={itemParam}/>}>
              <span className="d2p-skill-level">{' \u2013 '}{_L`Level ${level}`} ({_L(itemSkillTypes[itemType].format, itemParam)})</span>
            </Tooltip>
          )}
        </div>
        {!!disabled && <div className={`d2-color-${color}`}>{disabledText}</div>}
        {!disabled && func.showActive?.(id) && itemType !== 'aura' && <SkillSettings unit={unit} id={id} stats={stats} activeKey={activeKey}/>}
        {!disabled && isBuff && <BuffSettings unit={unit} id={id} active={!buffInactive} ref={clickRef} level={level}/>}
        {!disabled && isSummon && <SummonSettings id={id} active={!summonInactive} ref={summonRef} target={target} level={level}/>}
        {!disabled && !!info?.damage && <SkillDamage unit={unit} stats={stats} damage={info.damage}/>}
        {!disabled && !itemSkillTypes[itemType]?.proc && !!info?.animation && <SkillAnimation unit={unit} stats={stats} animation={info.animation}/>}
        {!disabled && !itemSkillTypes[itemType]?.proc && !!info?.dps && <SkillDPS dps={info.dps}/>}
      </div>
    </div>
  );
});
